import { useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import type { PickerUser } from '@/services/users.service';
import { SearchInput } from '@/components/ui/search-input';
import { cn } from '@/lib/utils';

const UNASSIGNED_DEPT = 'No Department';

function fullName(u: PickerUser): string {
  return `${u.first_name} ${u.last_name}`.trim();
}

interface Props {
  people: PickerUser[];
  value: string[];
  onChange: (ids: string[]) => void;
  /** Extra classes for the outer box, e.g. to let it flex inside a bounded column. */
  className?: string;
}

/**
 * Searchable multi-select of users for a shared ID, grouped by department, with the selection shown
 * as removable tags.
 *
 * Local to id-management on purpose: tasks' AssigneePicker and dm-team's DmUserMultiPicker are
 * similar, but importing across modules is banned. It renders inline so it keeps focus inside the
 * form's Dialog.
 */
export function IdUserMultiPicker({ people, value, onChange, className }: Props) {
  const [search, setSearch] = useState('');

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? people.filter((u) =>
          fullName(u).toLowerCase().includes(q) ||
          (u.employee_code ?? '').toLowerCase().includes(q) ||
          (u.department_name ?? '').toLowerCase().includes(q))
      : people;
    const map = new Map<string, PickerUser[]>();
    for (const u of filtered) {
      const dept = u.department_name ?? UNASSIGNED_DEPT;
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept)!.push(u);
    }
    for (const list of map.values()) list.sort((a, b) => fullName(a).localeCompare(fullName(b)));
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [people, search]);

  const selected = useMemo(
    () => value.flatMap((id) => people.filter((p) => p.id === id)),
    [people, value],
  );

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  return (
    <div className={cn('flex flex-col overflow-hidden rounded-lg border border-border/60 bg-card', className)}>
      <div className="shrink-0 border-b border-border/60 p-1.5">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, code or department…"
          containerClassName="w-full"
        />
      </div>

      {selected.length > 0 && (
        <div className="thin-scrollbar flex max-h-20 shrink-0 flex-wrap gap-1 overflow-y-auto overscroll-contain border-b border-border/60 p-2">
          {selected.map((u) => (
            <button
              type="button"
              key={u.id}
              aria-label={`Remove ${fullName(u)}`}
              onClick={() => toggle(u.id)}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-2 pr-1 text-xs font-medium text-foreground transition-colors hover:bg-primary/20"
            >
              <span className="truncate">{fullName(u)}</span>
              <X className="h-3 w-3 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}

      {/* Capped while the popup stacks into one column; from md: up it fills the column instead, so
          it is never a scroller inside another scroller. */}
      <div className="thin-scrollbar max-h-52 min-h-0 flex-1 overflow-y-auto overscroll-contain p-1 md:max-h-none">
        {grouped.length === 0 && (
          <p className="p-4 text-center text-sm text-muted-foreground">No users found</p>
        )}
        {grouped.map(([dept, users]) => (
          <div key={dept} className="mb-0.5">
            {/* z-[1] only has to beat its sibling rows while scrolling. */}
            <div className="sticky top-0 z-[1] bg-card/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
              {dept}
            </div>
            {users.map((u) => {
              const isSelected = value.includes(u.id);
              return (
                <button
                  type="button"
                  key={u.id}
                  role="checkbox"
                  aria-checked={isSelected}
                  onClick={() => toggle(u.id)}
                  className={cn(
                    'flex min-h-[44px] w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors',
                    isSelected ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/50',
                  )}
                >
                  <span className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                  )}>
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {fullName(u)}
                  </span>
                  {u.employee_code && (
                    <span className="shrink-0 text-xs text-muted-foreground">{u.employee_code}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
